<template>
  <div
    class="gallery flex flex-col sm:flex-row gap-2 md:gap-4 sm:h-[264px] lg:h-[420px] mt-4 sm:mt-6"
  >
    <div class="sm:w-full rounded-lg relative">
      <NuxtImg
        :height="'auto'"
        :preload="true"
        :src="String(mainMedia?.src?.mobile)"
        :alt="mainMedia?.alt"
        aspect-ratio="auto"
        loading="eager"
        class="sm:hidden w-full h-full object-cover rounded-lg"
        sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
      >
      </NuxtImg>
      <NuxtImg
        :height="'auto'"
        :preload="true"
        :src="String(mainMedia?.src?.desktop)"
        :alt="mainMedia?.alt"
        aspect-ratio="auto"
        loading="eager"
        class="hidden sm:block w-full h-full object-cover rounded-lg"
        sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
      >
      </NuxtImg>

      <div class="content">
        <h1 class="text-h1 font-semibold">{{ mainMedia.title }}</h1>
        <h2 class="text-base text-stone-700 font-medium mt-.05 mb-4">
          {{ mainMedia.subtitle }}
        </h2>
        <NuxtLazyHydrate when-idle>
          <FederatedSearchLoadingPlaceholder v-show="!isMounted" />
        </NuxtLazyHydrate>
        <NuxtLazyHydrate when-idle>
          <FederatedSearch
            v-show="isMounted"
            element-id="banner-autocomplete"
          />
        </NuxtLazyHydrate>

        <div class="animation">
          <div class="animation-item animation-item--1">
            <HomeAnimation1 />
          </div>

          <div class="animation-item animation-item--2">
            <HomeAnimation2 />
          </div>

          <div class="animation-item animation-item--3">
            <HomeAnimation3 />
          </div>
        </div>
      </div>
    </div>
    <div class="secondary-cards-container">
      <div
        v-for="(media, index) in secondaryMedia?.slice(0, 4)"
        :key="index"
        class="card-item"
        :class="{
          'sm:row-span-2': index === 2,
          'lg:h-[202px] sm:hidden': index !== 2,
        }"
      >
        <component
          :is="media.url ? 'a' : 'div'"
          :href="media.url"
          class="h-full"
        >
          <NuxtImg
            :height="'auto'"
            :preload="true"
            :src="String(media?.src?.mobile)"
            :alt="media.alt"
            aspect-ratio="auto"
            loading="lazy"
            class="h-full w-full object-cover sm:hidden"
            sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
          />
          <NuxtImg
            :height="'auto'"
            :preload="true"
            :src="String(media?.src?.desktop)"
            :alt="media.alt"
            aspect-ratio="auto"
            loading="lazy"
            class="h-full w-full object-cover hidden sm:block"
            sizes="xs:100vw sm:100vw md:100vw lg:910px xl:910px xxl:910px"
          />
          <div
            class="bg-blend-hard-light bg-gradient-to-t from-stone-700 to-transparent absolute bottom-0 left-0 w-full h-full overflow-hidden opacity-30 md:opacity-0 group-hover:opacity-10 transition-all duration-300"
          ></div>
          <div
            class="title absolute bottom-0 left-0 p-2 w-full font-semibold text-white text-sm"
          >
            {{ media.text }}
          </div>
        </component>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import HomeAnimation1 from '~/public/_nuxt3-static/animation/home-animation-1.svg?component'
import HomeAnimation2 from '~/public/_nuxt3-static/animation/home-animation-2.svg?component'
import HomeAnimation3 from '~/public/_nuxt3-static/animation/home-animation-3.svg?component'

const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

const homePageGalleryData = inject('homePageGalleryData') as ComputedRef<any>
const bannerData = computed(() => homePageGalleryData.value?.data?.banner)
const mainMedia = computed(() => {
  const mainMediaBlock = bannerData.value?.mainMediaBlock
  const alt = strapiImageGetters.getAlternativeText(mainMediaBlock?.media)

  return {
    src: {
      mobile:
        strapiImageGetters.getImageUrl(mainMediaBlock?.mediaMobile, 'small') ||
        strapiImageGetters.getImageUrl(mainMediaBlock?.media, 'large'),
      desktop: strapiImageGetters.getImageUrl(mainMediaBlock?.media, 'large'),
    },
    alt,
    title: mainMediaBlock?.title,
    subtitle: mainMediaBlock?.subtitle,
  }
})

const secondaryMedia = computed(() => {
  if (!bannerData.value?.secondaryMediaBlocks) return []
  return bannerData.value?.secondaryMediaBlocks
    .filter((mediaBlock) => mediaBlock?.media?.mime?.includes('image'))
    .map((mediaBlock) => ({
      src: {
        mobile:
          strapiImageGetters.getImageUrl(mediaBlock?.mediaMobile) ||
          strapiImageGetters.getImageUrl(mediaBlock?.media, 'large'),
        desktop: strapiImageGetters.getImageUrl(mediaBlock?.media, 'large'),
      },
      alt: strapiImageGetters.getAlternativeText(mediaBlock?.media),
      text: mediaBlock?.title,
      url: mediaBlock?.url,
    }))
})
</script>
<style lang="postcss" scoped>
.content {
  @apply absolute bottom-0 flex flex-col justify-center px-4 pb-4 sm:px-12;
  @apply md:absolute md:top-0 md:my-auto md:h-auto;
}
.secondary-cards-container {
  @apply grid grid-rows-1 sm:grid-rows-2 grid-flow-col gap-2;
  @apply overflow-x-auto;
  @media (min-width: 640px) {
    overflow-x: unset;
  }
}

.card-item {
  @apply rounded-lg cursor-pointer overflow-hidden w-[125px] h-[125px] sm:h-auto lg:w-[202px] md:block relative;

  img {
    @apply transition-all duration-300;
  }
  &:hover {
    img {
      @apply transform scale-105;
    }
  }
}

.title {
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: -10px;
    background: linear-gradient(-180deg, transparent, rgba(0, 0, 0, 0.7));
    z-index: -1;
  }
}

@keyframes item-animation {
  0%,
  100% {
    transform: rotate(0deg) translate3d(0, 0, 0);
  }
  20%,
  60% {
    transform: rotate(10deg) translate3d(-5px, 5px, 0);
  }

  40%,
  80% {
    transform: rotate(-15deg) translate3d(-15px, 0px, 0);
  }
}

@keyframes item-animation-small {
  0%,
  100% {
    transform: rotate(0deg) translate3d(0, 0, 0);
  }
  20%,
  60% {
    transform: rotate(10deg) translate3d(-10px, 3px, 0) scale(1.3);
  }

  40%,
  80% {
    transform: rotate(-10deg) translate3d(-3px, 5px, 0) scale(1.5);
  }
}

@keyframes item-animation-large {
  0%,
  100% {
    transform: rotate(0deg) translate3d(0, 0, 0);
  }
  20%,
  60% {
    transform: rotate(0deg) translate3d(3px, 5px, 0);
  }

  40%,
  80% {
    transform: rotate(-10deg) translate3d(5px, 0, 0) scale(0.9);
  }
}

.animation {
  @apply absolute top-0 left-0 right-0 bottom-0 pointer-events-none z-1;

  &-item {
    position: absolute;

    &--1 {
      @apply bottom-0 right-[-30px];
      animation: item-animation-large 15s ease infinite;

      @screen lg {
        @apply bottom-auto top-[185px] right-0;
      }
    }

    &--2 {
      @apply top-[120px] left-[0px];
      animation: item-animation-small 15s ease infinite;

      @screen lg {
        @apply top-[260px] left-[20px];
      }
    }

    &--3 {
      @apply top-[-50px] left-[47px];
      animation: item-animation 15s ease infinite;

      @screen lg {
        @apply top-[90px] left-[30px];
      }
    }
  }
}
</style>
